import { ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { EngagementStatusBadge } from '@/features/client/components/EngagementStatusBadge'
import { EngagementInvoicePanel } from './EngagementInvoicePanel'
import type {
  ClientEngagementDocument,
  CompanyMemberDocument,
  EquipmentDocument,
} from '@/types'

interface Props {
  engagement: ClientEngagementDocument & { id: string }
  members: Array<CompanyMemberDocument & { id: string }>
  equipment: Array<EquipmentDocument & { id: string }>
  onRefresh: () => void
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatCurrency(n: number | undefined): string {
  if (n === undefined || n === null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n)
}

function Field({
  label,
  children,
  span,
}: {
  label: string
  children: React.ReactNode
  span?: 1 | 2
}) {
  return (
    <div
      className={
        'space-y-1' + (span === 2 ? ' lg:col-span-2' : '')
      }
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function SectionCard({
  title,
  children,
  cols = 2,
}: {
  title: string
  children: React.ReactNode
  cols?: 1 | 2
}) {
  return (
    <section className="rounded-lg border bg-card px-5 py-4">
      <h3 className="text-sm font-semibold mb-3 text-foreground">{title}</h3>
      <div
        className={
          'grid gap-x-6 gap-y-4 ' + (cols === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1')
        }
      >
        {children}
      </div>
    </section>
  )
}

export function EngagementDetailsView({ engagement, members, equipment, onRefresh }: Props) {
  const memberById = new Map(members.map((m) => [m.id, m]))
  const equipmentById = new Map(equipment.map((e) => [e.id, e]))

  const assignedMemberIds =
    engagement.assignedMemberIds ??
    (engagement.assignedMemberId ? [engagement.assignedMemberId] : [])
  const assignedMembers = assignedMemberIds
    .map((id) => memberById.get(id))
    .filter((m): m is CompanyMemberDocument & { id: string } => !!m)

  const equipmentItems = (engagement.equipmentIds ?? [])
    .map((id) => equipmentById.get(id))
    .filter((e): e is EquipmentDocument & { id: string } => !!e)

  const lineItems = engagement.lineItems ?? []
  const subtotal = lineItems.reduce((s, li) => s + (li.lineTotal ?? 0), 0)
  const discountPct = engagement.discountPercent ?? 0
  const discountAmount = subtotal * (discountPct / 100)
  const total = engagement.estimateAmount ?? subtotal - discountAmount

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <header className="flex flex-wrap items-start justify-between gap-3 rounded-lg border bg-muted/40 px-5 py-4">
        <div className="space-y-1 min-w-0">
          <h2 className="text-lg font-semibold leading-tight truncate" title={engagement.title}>
            {engagement.title || '(untitled engagement)'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {engagement.clientName}{' '}
            <span className="text-muted-foreground/70">· {engagement.clientEmail}</span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <EngagementStatusBadge status={engagement.status} />
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            {formatDate(engagement.scheduledDate)}
          </p>
        </div>
      </header>

      {/* Details */}
      <SectionCard title="Details">
        <Field label="Description" span={2}>
          {engagement.description ? (
            <p className="whitespace-pre-wrap">{engagement.description}</p>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Field>
        <Field label="Scheduled date">{formatDate(engagement.scheduledDate)}</Field>
        <Field label="Status">
          <EngagementStatusBadge status={engagement.status} />
        </Field>
        <Field label="Assigned providers">
          {assignedMembers.length === 0 ? (
            <span className="text-muted-foreground">None assigned</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {assignedMembers.map((m) => (
                <Badge key={m.id} variant="secondary">
                  {m.name}
                </Badge>
              ))}
            </div>
          )}
        </Field>
        <Field label="Equipment">
          {equipmentItems.length === 0 ? (
            <span className="text-muted-foreground">None</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {equipmentItems.map((e) => (
                <Badge key={e.id} variant="outline">
                  {e.name}
                </Badge>
              ))}
            </div>
          )}
        </Field>
      </SectionCard>

      {/* Files */}
      <SectionCard title="Files" cols={1}>
        <Field label="Drive folder">
          {engagement.driveFolderUrl ? (
            <a
              href={engagement.driveFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
              title={engagement.driveFolderName}
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              {engagement.driveFolderName ?? 'Open folder'}
            </a>
          ) : engagement.driveLink ? (
            <a
              href={engagement.driveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline break-all"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              {engagement.driveLink}
            </a>
          ) : (
            <span className="text-muted-foreground">
              No folder yet (auto-created on acceptance or deposit payment)
            </span>
          )}
        </Field>
      </SectionCard>

      {/* Line Items */}
      <SectionCard title="Line Items" cols={1}>
        {lineItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No line items.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 px-2 font-medium">Description</th>
                  <th className="py-1.5 px-2 font-medium text-right">Qty</th>
                  <th className="py-1.5 px-2 font-medium text-right">Unit</th>
                  <th className="py-1.5 px-2 font-medium text-right">Unit Price</th>
                  <th className="py-1.5 px-2 font-medium text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="py-1.5 px-2">
                      {li.description}
                      {li.isAddon && (
                        <Badge variant="outline" className="ml-2 text-[10px] py-0">
                          Addon
                        </Badge>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{li.qty}</td>
                    <td className="py-1.5 px-2 text-right text-muted-foreground">{li.unit}</td>
                    <td className="py-1.5 px-2 text-right font-mono">
                      {formatCurrency(li.unitPrice)}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">
                      {formatCurrency(li.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="text-sm">
                <tr>
                  <td colSpan={4} className="py-1.5 px-2 text-right text-muted-foreground">
                    Subtotal
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">{formatCurrency(subtotal)}</td>
                </tr>
                {discountPct > 0 && (
                  <tr>
                    <td colSpan={4} className="py-1.5 px-2 text-right text-muted-foreground">
                      Discount ({discountPct}%)
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                      −{formatCurrency(discountAmount)}
                    </td>
                  </tr>
                )}
                <tr className="border-t">
                  <td colSpan={4} className="py-2 px-2 text-right font-semibold">
                    Total
                  </td>
                  <td className="py-2 px-2 text-right font-mono font-semibold">
                    {formatCurrency(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {engagement.estimateNote && (
          <div className="mt-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Estimate note
            </p>
            <p className="text-sm whitespace-pre-wrap">{engagement.estimateNote}</p>
          </div>
        )}
      </SectionCard>

      {/* Source */}
      {engagement.sourceRequestId && (
        <SectionCard title="Source" cols={1}>
          <Field label="Service request">
            <a
              href={`/admin/client-services/requests?focus=${engagement.sourceRequestId}`}
              className="text-primary hover:underline"
            >
              {engagement.sourceRequestId}
            </a>
          </Field>
        </SectionCard>
      )}

      {/* Invoicing — active actions intentionally kept available */}
      <EngagementInvoicePanel engagement={engagement} onRefresh={onRefresh} />
    </div>
  )
}
